import classNames from "classnames";
import React, { useState, useEffect, useRef } from "react";

export type CollectionItemMetadataProps = {
    title: string;
    metadata: {
        label: string;
        value: string | string[];
    }[];
    showMoreLabel: string;
    showLessLabel: string;
};

const MetadataItem: React.FC<{
    label: string;
    value: string | string[];
    isExpanded: boolean;
    isClamped: boolean;
    onToggleExpand: () => void;
    showMoreLabel: string;
    showLessLabel: string;
    bibliografiRef?: React.RefObject<HTMLSpanElement>;
}> = ({ label, value, isExpanded, isClamped, onToggleExpand, showMoreLabel, showLessLabel, bibliografiRef }) => {
    if (label === "Bibliography" || label === "Bibliografi") {
        return (
            <>
                <span
                    ref={bibliografiRef}
                    className={classNames("CollectionItemMetadata__bibliografi", {
                        expanded: isExpanded,
                        collapsed: !isExpanded,
                        "is-clamped": isClamped,
                    })}
                    dangerouslySetInnerHTML={{ __html: value as string }}
                ></span>
                {isClamped && (
                    <button
                        className="CollectionItemMetadata__toggle-button"
                        onClick={onToggleExpand}
                        aria-controls="bibliografi-content"
                        aria-expanded={isExpanded}
                    >
                        {isExpanded ? showLessLabel : showMoreLabel}
                    </button>
                )}
            </>
        );
    }

    if (Array.isArray(value)) {
        return (
            <>
                {value.map((val, index) => (
                    <span key={index}>
                        {val}
                        {index !== value.length - 1 && <br />}
                    </span>
                ))}
            </>
        );
    }

    return <>{value}</>;
};

export const CollectionItemMetadata = React.forwardRef<HTMLDivElement, CollectionItemMetadataProps>(
    ({ title, metadata, showLessLabel, showMoreLabel }, ref) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [isClamped, setIsClamped] = useState(false);

        const bibliografiRef = useRef<HTMLSpanElement>(null);

        useEffect(() => {
            if (bibliografiRef.current) {
                const { scrollHeight, clientHeight } = bibliografiRef.current;
                setIsClamped(scrollHeight > clientHeight);
            }
        }, [metadata]);

        const toggleExpand = () => {
            setIsExpanded((prevState) => !prevState);
        };

        const midpoint = Math.ceil(metadata.length / 2);
        const renderMetadataList = (data: typeof metadata) => (
            <dl className="CollectionItemMetadata__list">
                {data.map((item) => (
                    (
                        typeof item.value === "string" && item.value.trim().length !== 0 ||
                        Array.isArray(item.value) && item.value.length !== 0
                    ) &&
                    <li className="CollectionItemMetadata__list-item" key={item.label}>
                        <strong>{item.label}</strong>
                        <MetadataItem
                            label={item.label}
                            value={item.value}
                            isExpanded={isExpanded}
                            isClamped={isClamped}
                            onToggleExpand={toggleExpand}
                            showMoreLabel={showMoreLabel}
                            showLessLabel={showLessLabel}
                            bibliografiRef={
                                item.label === "Bibliography" || item.label === "Bibliografi"
                                    ? bibliografiRef
                                    : undefined
                            }
                        />
                    </li>
                ))}
            </dl>
        );

        return (
            <div ref={ref} className="CollectionItemMetadata" tabIndex={-1} aria-labelledby="metadata-title">
                <h2 id="metadata-title">{title}</h2>
                <div className="CollectionItemMetadata__container">
                    {renderMetadataList(metadata.slice(0, midpoint))}
                    {renderMetadataList(metadata.slice(midpoint))}
                </div>
            </div>
        );
    }
);
